import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Mountain, Truck, Lock, Wrench } from "lucide-react";
import { api, formatINR } from "../lib/api";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import JsonLd, { ORG_SCHEMA, WEBSITE_SCHEMA } from "../components/JsonLd";
import { toast } from "sonner";

const FEATURES = [
  { icon: Wrench, title: "Built By Riders", body: "Designed by riders who've earned every mile." },
  { icon: Mountain, title: "Adventure Tested", body: "Field-proven across Himalayas, deserts, monsoons." },
  { icon: Shield, title: "Premium Quality", body: "Materials and stitching engineered to outlast." },
  { icon: Truck, title: "Nationwide Delivery", body: "Fast shipping across India. COD available." },
  { icon: Lock, title: "Secure Checkout", body: "UPI, cards and Cashfree-grade security." },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1596687760372-4c0d266059a7?w=900",
  "https://images.unsplash.com/photo-1590506978404-d3e577192a18?w=900",
  "https://images.unsplash.com/photo-1690540293162-bfb097e68a34?w=900",
  "https://images.unsplash.com/photo-1575312363468-c8455fb38a76?w=900",
  "https://images.unsplash.com/photo-1755637042891-e73b82fac27b?w=900",
  "https://images.unsplash.com/photo-1552306062-29a5560e1c31?w=900",
];

const LADAKH_STORIES = [
  {
    slug: "khardung-la",
    location: "Khardung La · 5,359 m",
    title: "Crossing Khardung La",
    teaser: "Thin air, thinner margins. What it takes to ride the world's highest motorable pass — and come back stronger.",
    image: "https://images.unsplash.com/photo-1701599347330-f01207c56ca5?w=1200&q=80&auto=format&fit=crop",
    href: "https://rides.touringcraft.com",
  },
  {
    slug: "pangong-nights",
    location: "Pangong Tso · Ladakh",
    title: "Pangong Nights",
    teaser: "Camping beside a lake that changes colour seven times a day. A rider's guide to the blue silence.",
    image: "https://images.unsplash.com/photo-1624890240392-da0b1aa01c90?w=1200&q=80&auto=format&fit=crop",
    href: "https://rides.touringcraft.com",
  },
  {
    slug: "leh-diaries",
    location: "Leh · Monasteries & Markets",
    title: "The Leh Diaries",
    teaser: "Prayer flags, stupas and workshop coffee. Slow mornings before the long ride to Nubra.",
    image: "https://images.unsplash.com/photo-1670644654521-6fbfffeaba87?w=1200&q=80&auto=format&fit=crop",
    href: "https://rides.touringcraft.com",
  },
];

export default function Home() {
  const { categories } = useShop();
  const [featured, setFeatured] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } })
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatured([]));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Forge.");
      setEmail("");
    } catch { toast.error("Invalid email"); }
  };

  return (
    <div data-testid="home-page">
      <JsonLd data={ORG_SCHEMA} />
      <JsonLd data={WEBSITE_SCHEMA} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1670644654521-6fbfffeaba87?w=2400&q=80&auto=format&fit=crop"
            alt="Motorcycle traveller in full riding gear on a rustic Ladakh mountain road"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-transparent" />
          <div className="absolute inset-0 hero-shimmer pointer-events-none" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="overline mb-5">Mile Forge — Est. 2026</div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter font-bold leading-[0.92] max-w-4xl">
              Forged<br />Through <span className="text-[#D96A1B]">Miles</span>
            </h1>
            <p className="mt-6 max-w-xl text-white/75 font-body text-base sm:text-lg leading-relaxed">
              Premium adventure lifestyle products for riders, travellers, trekkers and explorers.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-now" className="btn-orange">
                Shop Now <ArrowRight size={18} />
              </Link>
              <Link to="/shop?category=adventure" data-testid="hero-explore" className="btn-ghost">
                Explore Collection
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-[10px] tracking-[0.3em] uppercase">scroll</div>
      </section>

      {/* TICKER */}
      <div className="ticker bg-[#0A0A0A] py-3">
        <div className="ticker-track font-display text-2xl tracking-[0.18em] uppercase text-white/80">
          {Array(8).fill("Mountains · Highways · Monsoons · Deserts · Trails · Camps · ").join("")}
        </div>
      </div>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex justify-between items-end mb-12">
          <div>
            <div className="overline mb-3">Shop By</div>
            <h2 className="section-title text-3xl sm:text-5xl">Featured Categories</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex font-display text-sm tracking-widest uppercase text-white/70 hover:text-[#D96A1B]">View All →</Link>
        </div>

        <div className="grid md:grid-cols-12 gap-5">
          {categories.slice(0, 4).map((c, i) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className={`group relative overflow-hidden ${
                i === 0 ? "md:col-span-7 md:row-span-2 aspect-[16/10] md:aspect-auto md:min-h-[560px]" : "md:col-span-5 aspect-[16/10] md:aspect-[16/9]"
              }`}
            >
              <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/90 via-black/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-7">
                <div className="overline mb-2">{c.subs.length} Products Lines</div>
                <h3 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white">{c.name}</h3>
                <p className="text-sm text-white/70 mt-2 font-body max-w-md">{c.tagline}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-[#D96A1B] font-display text-xs tracking-[0.2em] uppercase">
                  Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY MILEFORGE */}
      <section className="bg-[#111] border-y border-white/5 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col items-start gap-4 mb-14">
            <div className="overline">Why Mile Forge</div>
            <h2 className="section-title text-3xl sm:text-5xl max-w-2xl">Engineered for the long road.</h2>
            <div className="divider-orange" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {FEATURES.map((f) => (
              <div key={f.title} data-testid={`why-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`} className="border border-white/8 p-6 hover:border-[#D96A1B]/50 transition-colors">
                <f.icon size={28} className="text-[#D96A1B]" />
                <h3 className="mt-5 font-display text-sm uppercase tracking-widest">{f.title}</h3>
                <p className="mt-2 text-xs text-white/55 font-body leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex justify-between items-end mb-12">
          <div>
            <div className="overline mb-3">Selected</div>
            <h2 className="section-title text-3xl sm:text-5xl">Featured Products</h2>
          </div>
          <Link to="/shop" className="font-display text-sm tracking-widest uppercase text-white/70 hover:text-[#D96A1B]">All Products →</Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* FORGED IN THE HIMALAYAS — LADAKH STORY STRIP */}
      <section data-testid="ladakh-story-section" className="relative bg-[#0A0A0A] border-b border-white/5 py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #D96A1B 0%, transparent 45%), radial-gradient(circle at 80% 80%, #D96A1B 0%, transparent 45%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="overline mb-3 text-[#D96A1B]">Forged in the Himalayas</div>
              <h2 className="section-title text-3xl sm:text-5xl max-w-2xl">
                Stories from the <span className="text-[#D96A1B]">high passes.</span>
              </h2>
              <p className="mt-5 text-white/65 font-body max-w-xl leading-relaxed">
                Real rides. Real riders. Dispatched straight from Ladakh — curated by the Touring Craft community.
              </p>
            </div>
            <a
              href="https://rides.touringcraft.com"
              target="_blank"
              rel="noreferrer"
              data-testid="ladakh-view-all-stories"
              className="btn-ghost self-start lg:self-auto inline-flex"
            >
              Read All Stories <ArrowRight size={16} />
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {LADAKH_STORIES.map((story, i) => (
              <a
                key={story.slug}
                href={story.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`ladakh-story-card-${story.slug}`}
                className="group relative block overflow-hidden border border-white/8 hover:border-[#D96A1B]/60 transition-colors bg-[#111]"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={story.image}
                    alt={story.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute top-4 left-4 overline text-[#D96A1B] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#D96A1B] rounded-full animate-pulse" />
                    Chapter 0{i + 1}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <div className="overline mb-2 text-white/60">{story.location}</div>
                    <h3 className="font-display text-2xl lg:text-3xl uppercase tracking-tight leading-tight text-white">
                      {story.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/70 font-body leading-relaxed line-clamp-2">
                      {story.teaser}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-[#D96A1B] font-display text-xs tracking-[0.2em] uppercase">
                      Read Story <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TOURING CRAFT COMMUNITY STRIP */}
      <a
        href="https://rides.touringcraft.com"
        target="_blank"
        rel="noreferrer"
        data-testid="touring-craft-strip"
        className="group block border-y border-[#D96A1B]/30 bg-gradient-to-r from-[#D96A1B]/10 via-[#D96A1B]/5 to-[#D96A1B]/10 hover:from-[#D96A1B]/20 hover:via-[#D96A1B]/10 hover:to-[#D96A1B]/20 transition-colors"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-center gap-3 sm:gap-5 text-center flex-wrap">
          <span className="inline-flex items-center gap-2 overline text-[#D96A1B]">
            <span className="w-1.5 h-1.5 bg-[#D96A1B] animate-pulse rounded-full" />
            Community
          </span>
          <span className="font-display text-sm sm:text-base tracking-wider uppercase text-white">
            Powered by <span className="text-[#D96A1B]">5,000+ riders.</span> Join the Touring Craft community
          </span>
          <span className="inline-flex items-center gap-1 font-display text-xs tracking-[0.2em] uppercase text-[#D96A1B] group-hover:translate-x-1 transition-transform">
            Visit <ArrowRight size={14} />
          </span>
        </div>
      </a>

      {/* COMMUNITY */}
      <section className="relative bg-[#0A0A0A] py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="overline mb-3">The Community</div>
              <h2 className="section-title text-4xl sm:text-5xl">More Than<br />A Brand.</h2>
              <p className="mt-6 text-white/70 font-body leading-relaxed">
                We are a community of explorers, riders and adventure seekers who believe every journey shapes who we become. Tag <span className="text-[#D96A1B]">#ForgedThroughMiles</span> to be featured.
              </p>
              <Link to="/about" className="btn-ghost mt-7 inline-flex">Our Story</Link>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {GALLERY.map((src, i) => (
                  <div
                    key={src}
                    data-testid={`gallery-${i}`}
                    className={`overflow-hidden ${
                      i === 0 ? "row-span-2 aspect-[3/4]" : i === 3 ? "row-span-2 aspect-[3/4]" : "aspect-square"
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="relative py-20 lg:py-28">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1552306062-29a5560e1c31?w=2000" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="overline mb-4">Newsletter</div>
          <h2 className="section-title text-4xl sm:text-6xl">Join The Forge.</h2>
          <p className="mt-5 text-white/70 font-body">Get ride updates, product launches and exclusive offers — straight from the road.</p>
          <form onSubmit={subscribe} className="mt-8 flex max-w-lg mx-auto">
            <input
              data-testid="home-newsletter-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              placeholder="your@email.com"
              className="flex-1 bg-[#111] border border-white/15 px-4 py-3.5 font-body focus:outline-none focus:border-[#D96A1B] text-white"
            />
            <button data-testid="home-newsletter-submit" className="btn-orange px-7">Subscribe</button>
          </form>
        </div>
      </section>
    </div>
  );
}
